import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import {
  getAthleteNoteFormSchema,
  type AthleteNoteFormikType,
  type AthleteNoteFormType,
} from "@/modules/sessions/sessionForm/blocks/noteBlock/noteFormikTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { word } from "@modules/core/utils/i18n"
import { HURichTextInput } from "@modules/ui/components/richText/huRichTextInput"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { LoaderCircle, XIcon } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"

type Props = {
  updatedNote?: AthleteNoteFormikType
  updatedNoteIndex: number
  onValidate: (note: AthleteNoteFormikType) => Promise<void>
  className?: string
} & React.ComponentProps<"div">

export function NoteForAthletesForm({ updatedNote, updatedNoteIndex, onValidate, className, ...props }: Props) {
  const onCloseSidebarFnRef = useRef<() => void>(() => undefined)
  const onCloseNoteForAthleteFormFnRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true))
  const { updateSidebarStackOnTop, hideSidebarStackOnTop, hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()
  // Subscribe to language changes so a locale switch re-renders this form and rebuilds the schema
  // below with freshly translated validation messages.
  useLanguage()

  const athleteNoteFormSchema = getAthleteNoteFormSchema()

  const form = useAppForm({
    defaultValues:
      updatedNote ??
      ({
        id: null,
        note: "",
      } satisfies AthleteNoteFormType),
    validators: {
      onSubmit: athleteNoteFormSchema,
      onChange: athleteNoteFormSchema,
      onBlur: athleteNoteFormSchema,
    },
    onSubmit: async ({ value }) => {
      await onValidate(value)
    },
  })

  const onCloseNoteForAthleteForm = useCallback(
    async (shouldHideSidebar: boolean) => {
      // Close the sidebar top stack and hide the sidebar if the shouldHideSidebar is true
      const closeStack = async () => {
        hideSidebarStackOnTop()
        if (shouldHideSidebar) {
          // Wait for the sidebar top stack to be hidden before hiding the sidebar
          await new Promise((resolve) => setTimeout(resolve, 50))
          hideSidebar()
        }
      }

      // If there is no updated note, close the sidebar top stack and return false
      if (!updatedNote) {
        await closeStack()
        return Promise.resolve(false)
      } else {
        // If there is an updated note and the form is dirty, show the confirm popup
        if (form.state.isDirty) {
          const confirm = await new Promise<boolean>((resolve) => {
            confirmPopup.show({
              title: word("program.note.closeMessage.title"),
              message: word("program.note.closeMessage.message"),
              accept: async () => {
                await closeStack()
                resolve(false)
              },
              cancel: () => undefined,
              footerProps: {
                confirmText: word("global.ok"),
              },
            })
          })
          return confirm
        } else {
          // If there is an updated note and the form is not dirty, close the sidebar top stack and return false
          await closeStack()
          return Promise.resolve(false)
        }
      }
    },
    [updatedNote, confirmPopup, hideSidebarStackOnTop, hideSidebar, form],
  )

  // Defines the custom header buttons for the sidebar top stack
  const customHeaderButtons = [
    <Button
      key="close-note-for-athletes-form"
      variant="outline"
      size="iconMd"
      onClick={() => {
        onCloseSidebarFnRef.current()
      }}
    >
      <XIcon color="currentColor" size={16} />
    </Button>,
  ]

  useEffect(() => {
    // Add the custom header buttons and the left button click handler to the sidebar top stack
    updateSidebarStackOnTop({
      customHeaderButtons,
      sidebarLeftButtonClick: () => onCloseNoteForAthleteFormFnRef.current(),
    })
  }, [])

  useEffect(() => {
    // Set the onCloseSidebarFnRef and onCloseNoteForAthleteFormFnRef to the onCloseNoteForAthleteForm function
    onCloseSidebarFnRef.current = () => {
      void onCloseNoteForAthleteForm(true)
    }
    onCloseNoteForAthleteFormFnRef.current = () => {
      return onCloseNoteForAthleteForm(false)
    }
  }, [onCloseNoteForAthleteForm])

  const handleSubmit = async () => {
    // Validate every field with the "submit" cause so errors are surfaced (fields get marked as touched)
    await form.validateAllFields("submit")
    if (!form.state.isValid) return
    await form.handleSubmit()
  }

  return (
    <div className={cn("flex flex-1 flex-col justify-between gap-4 px-5 pt-18 pb-6", className)} {...props}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="text-textDefault-body uppercase">
            {`(${String(updatedNoteIndex + 1)}) ${word("program.planning.create.exercise.block_plural", { count: updatedNoteIndex })}`}
          </Text>

          <Text fontStyle="TL">{word("sessionForm.blocks.add_note_for_athletes.title")}</Text>
          <Text fontStyle="BS">{word("sessionForm.blocks.add_note_for_athletes.description")}</Text>
        </div>

        {/* Note content input */}
        <div className="card p-3">
          <form.AppField
            name="note"
            validators={{ onChange: athleteNoteFormSchema.shape.note, onBlur: athleteNoteFormSchema.shape.note }}
          >
            {(field) => (
              <HURichTextInput
                value={formatTextToRichText(field.state.value ?? "")}
                onChange={(value) => {
                  // HURichTextInput always emits an HTML string; its props intersect ComponentProps<"div">
                  // which widens onChange's param, so narrow it back before handing it to the field.
                  if (typeof value === "string") field.handleChange(value)
                }}
                label={word("sessionForm.blocks.add_note_for_athletes.note_content")}
                isRequiredInput
                maxLength={65000}
                paragrapheFontStyle="BM"
                isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
                error={<field.Message />}
              />
            )}
          </form.AppField>
        </div>
      </div>

      <div className="flex flex-row items-center justify-between gap-1">
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
              onClick={() => {
                void handleSubmit()
              }}
            >
              {isSubmitting ? <LoaderCircle className="animate-spin" /> : null}
              {updatedNote ? word("global.save") : word("global.check")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  )
}
